import {
	HttpClient,
	HttpResponse
}                              from '@angular/common/http';
import { Injectable }          from '@angular/core';
import { AbstractApiService }  from '@app/core/http/abstract-api.service';
import { SanitizerService }    from '@app/core/http/sanitizer.service';
import { TransitionService }   from '@app/core/http/transition.service';
import {
	IOrganizationHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState
}                              from '@app/core/interfaces/api.interface';
import { ModelTypeEnum }       from '@app/core/models/transition.model';
import { Organization }        from '@app/modules/organization/models/organization.model';
import { OrganizationService } from '@app/modules/organization/services/organization.service';
import { Address }             from '@app/shared/components/address/models/address.model';
import { LoggerService }       from '@app/shared/services/logger.service';
import { MapperService }       from '@http/mapper.service';
import { IHttpContextConfig }  from '@interceptors/interceptors.model';
import { SalutationEnum }      from '@models/common.model';
import {
	ClsOrganizationUpdateBodyDTO,
	OrganizationUpdateBodyDTO
}                              from '@openApiGenerated/admin-api';
import {
	AddressDataDTO,
	ClsOrganizationBodyDTO,
	ClsOrganizationStatusDTO,
	CreateOrganizationRequestParams,
	DeleteOrganizationRequestParams,
	GetOrganizationOrganizationsRequestParams,
	GetOrganizationRequestParams,
	GetTopLevelOrganizationsRequestParams,
	InvalidEntitiesDTO,
	OrganizationBodyDTO,
	OrganizationDTO,
	OrganizationsApiClient,
	OrganizationStatusDTO,
	SalutationEnumDTO,
	UpdateOrganizationRequestParams,
	UpdateOrganizationStatusRequestParams
}                              from '@openApiGenerated/management-api';
import { Observable }          from 'rxjs';

@Injectable( {
	providedIn: 'root'
} )
export class OrganizationApiService extends AbstractApiService {

	public organizationServiceInstance: OrganizationService;

	constructor(
		protected http: HttpClient,
		protected logger: LoggerService,
		private readonly mapperService: MapperService,
		private readonly organizationsApiClient: OrganizationsApiClient,
		private readonly sanitize: SanitizerService,
		private readonly transition: TransitionService
	) {
		super();
		this.http = http;
	}

	// SANITIZER ######################################
	private sanitizeApiResponse( entity: Organization, context = this ): void {
		if ( entity instanceof Organization ) {
			entity.addressData = context.sanitize.address( entity.addressData, ModelTypeEnum.TMO ) as Address;
			entity.createdDate = context.sanitize.date<Date>( entity.createdDate, ModelTypeEnum.TMO, Date );
			entity.lastModifiedDate = context.sanitize.date<Date>( entity.lastModifiedDate, ModelTypeEnum.TMO, Date );

			if ( entity.errors.invalidEntities ) {
				entity.errors.invalidEntities = context.mapperService.errors.invalidEntitiesDtoToTmo( entity.errors.invalidEntities as unknown as InvalidEntitiesDTO[] );
			}

			if ( context.organizationServiceInstance.check.isNaturalPerson( entity ) ) {
				entity.naturalPerson.birthDate = context.sanitize.date<Date>( entity.naturalPerson?.birthDate, ModelTypeEnum.TMO, Date );
				entity.naturalPerson.salutation = context.mapperService.common.salutationDtoToTmo( entity.naturalPerson?.salutation as unknown as SalutationEnumDTO );
			}
		}
	}

	private sanitizeForApiRequest( dto: OrganizationBodyDTO | OrganizationUpdateBodyDTO, context = this ): void {
		if ( dto instanceof ClsOrganizationBodyDTO || dto instanceof ClsOrganizationUpdateBodyDTO ) {
			dto.addressData = context.sanitize.address( dto.addressData, ModelTypeEnum.DTO ) as AddressDataDTO;
			dto.taxableEntity = dto.taxableEntity === true;

			if ( context.organizationServiceInstance.check.isNaturalPersonDto( dto ) ) {
				dto.naturalPerson.birthDate = context.sanitize.date<string>( dto.naturalPerson?.birthDate, ModelTypeEnum.DTO );
				dto.naturalPerson.salutation = context.mapperService.common.salutationTmoToDto( dto.naturalPerson?.salutation as unknown as SalutationEnum );
			}
		}
	}


	// LIST TYPE REQUESTS #############################
	public list( throwError = false ): Observable<RequestCompleteListState<Organization>> {
		this.logger.trace( 'pre-list call' );
		const requestParam: GetTopLevelOrganizationsRequestParams = {
			filter: null,
			hasValidationResults: null,
			organizationIds: null,
			pageNumber: null,
			pageSize: null,
			search: null,
			sort: 'lastModifiedDate,desc',
			status: null,
			taxableEntity: null
		};

		const req$ = this.organizationsApiClient.getTopLevelOrganizations( requestParam );
		return this.transition.handleListRequestResponse<OrganizationDTO, Organization>(
			req$,
			Organization,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public search(
		params: IOrganizationHttpParams,
		httpContextConfig: IHttpContextConfig,
		throwError = false
	): Observable<RequestCompleteListState<Organization>> {
		this.logger.trace( 'pre-search call: ', URL );
		const requestParam: GetTopLevelOrganizationsRequestParams = {
			filter: params?.filter,
			hasErrors: params?.hasErrors,
			hasValidationResults: params?.hasValidationResults,
			organizationIds: params?.organizationIds,
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.search ),
			sort: params?.sort,
			status: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status ),
			taxableEntity: params?.taxableEntity ? params?.taxableEntity[ 0 ] : null
		};

		const req$ =
			this.organizationsApiClient.getTopLevelOrganizations(
				requestParam,
				'body',
				false,
				this.buildHttpContext( httpContextConfig )
			);

		return this.transition.handleListRequestResponse<OrganizationDTO, Organization>(
			req$,
			Organization,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public listChildren( id: string, params?: IOrganizationHttpParams ): Observable<RequestCompleteListState<Organization>> {
		this.logger.trace( 'pre-listChildren call: ' );
		const requestParam: GetOrganizationOrganizationsRequestParams = {
			organizationId: id,
			hasValidationResults: params?.hasValidationResults,
			pageNumber: params?.paging?.pageNumber,
			pageSize: params?.paging?.pageSize,
			sort: params?.sort,
		};

		const req$ = this.organizationsApiClient.getOrganizationOrganizations( requestParam );
		return this.transition.handleListRequestResponse<OrganizationDTO, Organization>(
			req$, Organization, this, this.sanitizeApiResponse
		);
	}

	// ENTITY TYPE REQUESTS ###########################
	public read(
		organizationId: string,
		options: {
			httpContextConfig?: IHttpContextConfig,
			throwError?: boolean,
			withFullResponse?: boolean,
		}
	): Observable<RequestCompleteEntityState<Organization>> {
		this.logger.trace( 'pre-read call: ', organizationId );
		const { httpContextConfig, throwError = false, withFullResponse } = options;

		const requestParam: GetOrganizationRequestParams = {
			organizationId
		};

		const req$ = this.organizationsApiClient.getOrganization(
			requestParam,
			withFullResponse ? 'response' : undefined,
			false,
			this.buildHttpContext( httpContextConfig )
		);

		return this.transition.handleEntityRequestResponse<OrganizationDTO | HttpResponse<OrganizationDTO>, Organization>(
			req$,
			RequestActionEnum.READ,
			Organization,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public create( org: Organization ): Observable<RequestCompleteEntityState<Organization>> {
		this.logger.trace( 'pre-create call: ', { ...org } );
		const organizationBodyDTO = this.transition.prepareEntityRequest<Organization, OrganizationBodyDTO>(
			org, ClsOrganizationBodyDTO, this, this.sanitizeForApiRequest
		);
		const requestParam: CreateOrganizationRequestParams = {
			organizationBodyDTO
		};


		const req$ = this.organizationsApiClient.createOrganization( requestParam );
		return this.transition.handleEntityRequestResponse<OrganizationDTO, Organization>(
			req$,
			RequestActionEnum.CREATE,
			Organization,
			this,
			this.sanitizeApiResponse
		);
	}

	public update( org: Organization ): Observable<RequestCompleteEntityState<Organization>> {
		this.logger.trace( 'pre-update call: ', { ...org } );
		const organizationUpdateBodyDTO = this.transition.prepareEntityRequest<Organization, OrganizationUpdateBodyDTO>(
			org, ClsOrganizationUpdateBodyDTO, this, this.sanitizeForApiRequest
		);
		const requestParam: UpdateOrganizationRequestParams = {
			organizationId: org.id,
			ifMatch: org.etag,
			organizationUpdateBodyDTO
		};

		const req$ = this.organizationsApiClient.updateOrganization( requestParam );
		return this.transition.handleEntityRequestResponse<OrganizationDTO, Organization>(
			req$,
			RequestActionEnum.UPDATE,
			Organization,
			this,
			this.sanitizeApiResponse
		);
	}


	public changeStatus( org: Organization, throwError = false ): Observable<RequestCompleteEntityState<Organization>> {
		this.logger.trace( 'pre-changeStatus call: ', { ...org } );
		const organizationStatusDTO = this.transition.prepareEntityRequest<Organization, OrganizationStatusDTO>(
			org, ClsOrganizationStatusDTO, this, this.sanitizeForApiRequest
		);
		const requestParam: UpdateOrganizationStatusRequestParams = {
			organizationId: org.id,
			ifMatch: org.etag,
			organizationStatusDTO
		};

		const req$ = this.organizationsApiClient.updateOrganizationStatus( requestParam );
		return this.transition.handleEntityRequestResponse<OrganizationDTO | HttpResponse<OrganizationDTO>, Organization>(
			req$,
			RequestActionEnum.PATCH,
			Organization,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public delete( organizationId: string, throwError = false ): Observable<RequestCompleteEntityState<Organization>> {
		this.logger.trace( 'pre-delete call: ', organizationId );
		const requestParam: DeleteOrganizationRequestParams = {
			organizationId
		};

		const req$ = this.organizationsApiClient.deleteOrganization( requestParam, 'response' );
		return this.transition.handleEntityRequestResponse<HttpResponse<any>, Organization>(
			req$,
			RequestActionEnum.DELETE,
			Organization,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}
}
